import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TemplatePreviewProps } from './types';

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  isOpen,
  onClose,
  formData,
  fieldDefinitions
}) => {
  if (!isOpen) return null;

  // Get ordered visible fields
  const getOrderedVisibleFields = () => {
    const fields = formData.fieldsConfig?.fields || {};
    return Object.entries(fields)
      .filter(([_, config]) => config.visible)
      .map(([key, config]) => ({
        key,
        config,
        definition: fieldDefinitions[key]
      }))
      .filter(field => field.definition !== undefined)
      .sort((a, b) => a.config.order - b.config.order);
  };

  const orderedFields = getOrderedVisibleFields();
  const requiredCount = orderedFields.filter(f => f.config.required).length;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="preview-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="bg-white px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h3 id="preview-modal-title" className="text-xl font-bold text-gray-900">
                    Form Preview
                  </h3>
                  <p className="text-sm text-gray-600">
                    Exactly how your form will appear to users
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                aria-label="Close preview"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>


          {/* Preview Content */}
          <div className="px-6 py-8 max-h-[600px] overflow-y-auto bg-gray-50">
            <div className="max-w-2xl mx-auto">
              {/* Form Header - Exactly as it appears */}
              <div className="bg-white rounded-t-xl border border-gray-200 px-8 py-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-300">
                    <svg className="w-7 h-7 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {formData.name || 'New Lead Form'}
                    </h2>
                    {formData.description && (
                      <p className="text-sm text-gray-600 mt-1">
                        {formData.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Your information is secure and confidential</span>
                </div>
              </div>

              {/* Form Body - Exactly as it appears */}
              <div className="bg-white px-8 py-6 border-l border-r border-gray-200">

              {orderedFields.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-4 text-sm text-gray-500 font-medium">No fields selected</p>
                  <p className="text-xs text-gray-400">Add some fields to see how your form will look</p>
                </div>
              ) : (
                <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
                  {orderedFields.map((field, index) => {
                    const { key, config, definition } = field;
                    const isEven = index % 2 === 0;

                    return (
                      <div key={key} className={`${isEven && index + 1 < orderedFields.length && orderedFields[index + 1] ? 'sm:inline-block sm:w-[48%] sm:mr-[4%]' : 'block w-full'}`}>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">
                          {definition.label}
                          {config.required && <span className="text-red-500 ml-1">*</span>}
                        </label>

                        {definition.type === 'textarea' ? (
                          <textarea
                            rows={4}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            placeholder={`Enter ${definition.label.toLowerCase()}...`}
                            defaultValue=""
                          />
                        ) : definition.type === 'select' ? (
                          <select
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                          >
                            <option value="">Select {definition.label.toLowerCase()}</option>
                            {definition.options?.map((option, idx) => (
                              <option key={idx} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : definition.type === 'multiselect' ? (
                          <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white min-h-[48px] flex items-center">
                            <span className="text-gray-400 text-sm">Click to select multiple options...</span>
                          </div>
                        ) : definition.type === 'tags' ? (
                          <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white min-h-[48px] flex items-center">
                            <span className="text-gray-400 text-sm">Add tags...</span>
                          </div>
                        ) : definition.type === 'date' ? (
                          <input
                            type="date"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                          />
                        ) : definition.type === 'tel' ? (
                          <input
                            type="tel"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            placeholder={`e.g., +1 (555) 123-4567`}
                          />
                        ) : definition.type === 'email' ? (
                          <input
                            type="email"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            placeholder={`e.g., john@example.com`}
                          />
                        ) : (
                          <input
                            type={definition.type}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            placeholder={`Enter ${definition.label.toLowerCase()}...`}
                          />
                        )}
                      </div>
                    );
                  })}

                </form>
              )}
              </div>

              {/* Form Footer - Exactly as it appears */}
              {orderedFields.length > 0 && (
                <div className="bg-white px-8 py-6 rounded-b-xl border border-gray-200 border-t-0">
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2 text-gray-600 text-sm">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="font-medium">
                        {orderedFields.filter(f => f.config.required).length} required field{orderedFields.filter(f => f.config.required).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 shadow-sm hover:shadow-md transition-all"
                    >
                      Submit Lead →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white px-6 py-5 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span><strong>{Object.keys(fieldDefinitions).length}</strong> total fields</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span><strong>{orderedFields.length}</strong> visible</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-red-500 text-lg">*</span>
                <span><strong>{requiredCount}</strong> required</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors shadow-sm"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
